import React, { useState } from 'react'
import { useTheme } from '@mui/material/styles'
import { DataGridPro, gridClasses, GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid-pro'
import { getMuiDataGridLocaleText } from '../../i18n/localeUtils'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import { Link } from 'react-router'
import * as datefns from 'date-fns'
import ConnectCustomerDialog, { ConnectCustomerForm } from '../../pages/admin/users/ConnectCustomerDialog'
import UserApi from '../../api/UserApi'
import useConfirmationDialog from '../dialogs/ConfirmationDialog'
import PropTypes from 'prop-types'
import PasswordField from '../../pages/admin/users/PasswordField'
import UserActionsMenu from './UserActionsMenu'
import { UserCategoryDialog } from '../../pages/admin/users/UserCategoryDialog'
import { CustomDataGridToolbar } from '../custom-grid-components/CustomDataGridToolbar'
import {
    UserCategoryType,
    userCategoryAdminLabel,
    userHasManyDevices,
    UserEntity,
    UserListItem,
} from '@finance-lab/utils/types'
import UserDevicesDialog from '../../pages/admin/users/UserDevicesDialog'

const userColumns = ({
    fields,
    onSetUserCategory,
    onConnectClick,
    onDisconnectClick,
    onDeleteUser,
    onChangePassword,
    onViewDevices,
    showDeviceCount,
}: {
    fields?: Array<string>
    onSetUserCategory: (u: UserEntity) => void
    onConnectClick: (u: UserEntity) => void
    onDisconnectClick: (u: UserEntity) => void
    onDeleteUser: (id: string | number) => Promise<void>
    onChangePassword: (u: UserEntity) => void
    onViewDevices?: (u: UserEntity) => void
    showDeviceCount?: boolean
}) => {
    const cols: GridColDef<UserEntity>[] = [
        {
            field: 'name',
            headerName: 'Namn',
            minWidth: 150,
            flex: 1,
            renderCell: ({ value, row }) => <Link to={`/admin/users/${row.id}`}>{value || row.email}</Link>,
        },
        {
            field: 'email',
            headerName: 'Email',
            minWidth: 150,
            flex: 1,
            renderCell: (params) => <Link to={`mailto:${params.value}`}>{params.value}</Link>,
        },
        {
            field: 'customerName',
            headerName: 'Kund',
            minWidth: 150,
            flex: 1,
            renderCell: ({ value, row }) => {
                if (value) {
                    return <Link to={`/admin/customers/${row.customerId}`}>{value}</Link>
                }
                if (onConnectClick) {
                    return (
                        <Button variant='outlined' color='primary' size='small' onClick={() => onConnectClick(row)}>
                            Koppla
                        </Button>
                    )
                }
                return null
            },
        },
        {
            field: 'userCategory',
            headerName: 'Kategori',
            minWidth: 150,
            flex: 1,
            valueGetter: (_value, row) => userCategoryAdminLabel(row.userCategory),
        },
        {
            field: 'lastActive',
            headerName: 'Senast aktiv',
            minWidth: 150,
            flex: 1,
            valueFormatter: (value) => (value ? datefns.format(new Date(value), 'yyyy-MM-dd HH:mm:ss') : ''),
        },
        ...(showDeviceCount
            ? [
                  {
                      field: 'deviceCount',
                      headerName: 'Enheter',
                      width: 100,
                      type: 'number' as const,
                      renderCell: ({ row }: { row: UserEntity }) =>
                          onViewDevices ? (
                              <Button size='small' variant='text' onClick={() => onViewDevices(row)}>
                                  {row.deviceCount ?? 0}
                              </Button>
                          ) : (
                              (row.deviceCount ?? 0)
                          ),
                  } satisfies GridColDef<UserEntity>,
              ]
            : []),
        {
            field: 'actions',
            headerName: '',
            minWidth: 30,
            flex: 0.5,
            sortable: false,
            renderCell: ({ row }) => (
                <UserActionsMenu
                    user={row}
                    onSetUserCategory={onSetUserCategory}
                    onDisconnectClick={onDisconnectClick}
                    onDeleteUser={onDeleteUser}
                    onChangePassword={onChangePassword}
                    onViewDevices={onViewDevices}
                />
            ),
        },
    ]

    if (fields?.length > 0) {
        return cols.filter((col) => fields.includes(col.field))
    }

    return cols
}

const userApi = new UserApi()

function UsersGrid({
    fields,
    users,
    highlightManyDevices,
    loading,
    paginationMode = 'client',
    paginationModel,
    onPaginationModelChange,
    rowCount,
    onRefreshPage,
    sortingMode = 'client',
    sortModel,
    onSortModelChange,
}: {
    fields?: Array<keyof UserListItem>
    setUsers: (u: UserListItem[]) => void
    users: UserListItem[] | null
    /** Highlight rows where device count exceeds {@link USER_MULTI_DEVICE_THRESHOLD}. */
    highlightManyDevices?: boolean
    loading?: boolean
    paginationMode?: 'client' | 'server'
    paginationModel?: GridPaginationModel
    onPaginationModelChange?: (model: GridPaginationModel) => void
    rowCount?: number
    onRefreshPage?: () => Promise<void>
    sortingMode?: 'client' | 'server'
    sortModel?: GridSortModel
    onSortModelChange?: (model: GridSortModel) => void
}) {
    const { i18n } = useTranslation()
    const locale = i18n.language
    const theme = useTheme()
    const { confirm, ConfirmationDialog } = useConfirmationDialog()

    const [selectedUser, setSelectedUser] = useState<UserEntity | null>(null)
    const [connectCustomerDialogOpen, setConnectCustomerDialogOpen] = useState(false)
    const [userCategoryDialogOpen, setUserCategoryDialogOpen] = useState(false)

    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false)
    const [selectedUserForPassword, setSelectedUserForPassword] = useState<UserEntity | null>(null)
    const [newPassword, setNewPassword] = useState('')
    const [devicesDialogOpen, setDevicesDialogOpen] = useState(false)
    const [selectedUserForDevices, setSelectedUserForDevices] = useState<UserEntity | null>(null)

    const showDeviceCount = !fields?.length || fields.includes('deviceCount')

    const refreshUsers = async () => {
        if (onRefreshPage) {
            await onRefreshPage()
            return
        }
    }

    const handleViewDevices = (user: UserEntity) => {
        setSelectedUserForDevices(user)
        setDevicesDialogOpen(true)
    }

    const handleCloseDevicesDialog = () => {
        setDevicesDialogOpen(false)
        setSelectedUserForDevices(null)
    }

    const handleConnectClick = (user: UserEntity) => {
        setSelectedUser(user)
        setConnectCustomerDialogOpen(true)
    }

    const handleDisconnectClick = async (user: UserEntity) => {
        const confirmation = await confirm('Är du säker på att du vill koppla bort användaren från kunden?')
        if (!confirmation) return
        await userApi.disconnectFromCustomer(user.id)
        await refreshUsers()
    }

    const handleConnectCustomerDialogClose = () => {
        setConnectCustomerDialogOpen(false)
        setSelectedUser(null)
    }

    const handleUserCategoryDialogClose = () => {
        setUserCategoryDialogOpen(false)
        setSelectedUser(null)
    }

    const handleCustomerSubmit = async (data: ConnectCustomerForm) => {
        // Logic to update the user with the selected customer
        await userApi.connectToCustomer(selectedUser.id, data.customer.id)
        await refreshUsers()
        handleConnectCustomerDialogClose()
    }

    const handleDeleteUser = async (userId: string | number) => {
        const confirmation = await confirm('Är du säker på att du vill ta bort användaren?')
        if (!confirmation) return
        await userApi.adminDeleteUser(userId)
        await refreshUsers()
    }

    const handleOpenUserCategoryDialog = (user: UserEntity) => {
        setSelectedUser(user)
        setUserCategoryDialogOpen(true)
    }

    const handleOpenPasswordDialog = (user: UserEntity) => {
        setSelectedUserForPassword(user)
        setPasswordDialogOpen(true)
    }

    const handleClosePasswordDialog = () => {
        setPasswordDialogOpen(false)
        setSelectedUserForPassword(null)
        setNewPassword('')
    }

    const handleChangePassword = async () => {
        if (!newPassword) {
            alert('Please enter a new password.')
            return
        }
        try {
            await userApi.adminSetUserPassword(selectedUserForPassword.id, newPassword)
            alert('Password changed successfully.')
            handleClosePasswordDialog()
        } catch (error) {
            console.error('Failed to change password:', error)
            alert('Failed to change password.')
        }
    }

    const handleUserCategorySave = async (
        newCategory: UserCategoryType,
        trialPeriod?: { startsAt: Date; endsAt: Date },
    ) => {
        if (!selectedUser) return
        await userApi.adminSetUserCategory(selectedUser.id, newCategory, trialPeriod)
        await refreshUsers()
        handleUserCategoryDialogClose()
    }

    return (
        <div style={{ width: '100%' }}>
            <ConfirmationDialog />
            {/* Password Change Dialog */}
            <Dialog open={passwordDialogOpen} onClose={handleClosePasswordDialog}>
                <DialogTitle>Ändra lösenord för {selectedUserForPassword?.name}</DialogTitle>
                <DialogContent>
                    <PasswordField
                        label='Nytt lösenord'
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        autoFocus
                        showGeneratePassword={true}
                        sx={{ my: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePasswordDialog} color='secondary'>
                        Avbryt
                    </Button>
                    <Button onClick={handleChangePassword} color='primary'>
                        Ändra
                    </Button>
                </DialogActions>
            </Dialog>
            {selectedUser && (
                <UserCategoryDialog
                    open={userCategoryDialogOpen}
                    onClose={handleUserCategoryDialogClose}
                    userEmail={selectedUser.email}
                    initialUserCategory={selectedUser.userCategory}
                    initialTrialPeriod={null}
                    onSave={handleUserCategorySave}
                />
            )}
            <DataGridPro
                sx={{
                    [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
                        outline: 'none',
                    },
                    [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
                        outline: 'none',
                    },
                    width: '100%',
                    '& .even': {
                        backgroundColor: theme.palette.rowColor.main, // Use palette color for even rows
                    },
                    ...(highlightManyDevices && {
                        '& .many-devices': {
                            backgroundColor: theme.palette.warning.light,
                        },
                    }),
                }}
                rows={users ?? []}
                columns={userColumns({
                    fields,
                    onSetUserCategory: handleOpenUserCategoryDialog,
                    onConnectClick: handleConnectClick,
                    onDisconnectClick: handleDisconnectClick,
                    onDeleteUser: handleDeleteUser,
                    onChangePassword: handleOpenPasswordDialog,
                    onViewDevices: handleViewDevices,
                    showDeviceCount,
                })}
                localeText={getMuiDataGridLocaleText(locale)}
                // Use the custom toolbar
                slots={{ toolbar: CustomDataGridToolbar }}
                loading={loading ?? !users}
                pagination={paginationMode === 'server'}
                paginationMode={paginationMode}
                paginationModel={paginationModel}
                onPaginationModelChange={onPaginationModelChange}
                rowCount={rowCount}
                pageSizeOptions={[25, 50, 100]}
                sortingMode={sortingMode}
                sortModel={sortModel}
                onSortModelChange={onSortModelChange}
                slotProps={{
                    loadingOverlay: {
                        variant: 'skeleton',
                        noRowsVariant: 'skeleton',
                    },
                }}
                getRowClassName={(params) => {
                    const stripe = params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                    if (highlightManyDevices && userHasManyDevices(params.row.deviceCount ?? 0)) {
                        return `${stripe} many-devices`
                    }
                    return stripe
                }}
                // Disable column filter options
                disableColumnFilter
                // Disable column menu
                disableColumnMenu
                // Disable row selection on click
                disableRowSelectionOnClick
                showToolbar
            />
            <ConnectCustomerDialog
                open={connectCustomerDialogOpen}
                onClose={handleConnectCustomerDialogClose}
                onSubmit={handleCustomerSubmit}
            />
            <UserDevicesDialog
                open={devicesDialogOpen}
                userId={selectedUserForDevices?.id ?? null}
                userName={selectedUserForDevices?.name ?? null}
                onClose={handleCloseDevicesDialog}
            />
        </div>
    )
}

UsersGrid.propTypes = {
    fields: PropTypes.arrayOf(PropTypes.string), // Optional array of strings
    users: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired, // User ID, required
            name: PropTypes.string.isRequired, // User name, required
            email: PropTypes.string, // Email, optional
            customerName: PropTypes.string, // Customer name, optional
            customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Customer ID, optional
            lastActive: PropTypes.string, // ISO date string, optional
            disabled: PropTypes.bool, // Whether the user is disabled, optional
        }),
    ), // Array of user objects, required
    setUsers: PropTypes.func.isRequired, // Function to update the list of users, required
}

export default UsersGrid
